import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { useFileUpload } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { UploadSuccessCallback, UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { IMAGE_MIME } from '@wix/da-shared-react/pkg/utils/mime';
import { useAttachmentManager } from '../../hooks/useAttachmentManager';
import EmptyState from './EmptyState';
import Preview from './Preview';

import s from './MultiImageUploader.scss';

interface Props {
  deviationId?: number | React.RefObject<number | undefined>;
  deviation?: PapiDeviation;
  className?: string;
  isOriginals?: boolean;
  emptyStateHasError?: boolean;
  onBeforeUpload?: () => Promise<boolean>;
  onAfterUpload?: (deviation: PapiDeviation) => void;
}

const MultiImageUploader: React.FC<Props> = ({
  deviationId: deviationIdFromProps,
  deviation: deviationFromProps,
  className,
  isOriginals = false,
  emptyStateHasError,
  onBeforeUpload,
  onAfterUpload,
}) => {
  const [deviation, setDeviation] = useState<PapiDeviation | undefined>(deviationFromProps);

  const onSourceFileUpload: UploadSuccessCallback = useCallback(
    response => {
      setDeviation(response.deviation);
      onAfterUpload && response.deviation && onAfterUpload(response.deviation);
    },
    [onAfterUpload]
  );
  const onAttachmentAdded = useCallback(
    (_, updatedDeviation) => setDeviation(updatedDeviation),
    []
  );

  const deviationId = deviationFromProps ? deviationFromProps.deviationId : deviationIdFromProps;
  const {
    input: sourceFileInput,
    getDropzoneRootProps: getSourceFileRootProps,
    activeUploads: activeSourceFileUploads,
  } = useFileUpload({
    id: 'multi-image-uploader-source-file',
    uploadType: deviationId ? UploadType.DEVIATION_REPLACE_ORIGINAL : UploadType.DEVIATION_NEW,
    uploadParams: {
      deviationId,
    },
    allowMultiple: false,
    allowDragAndDrop: false,
    acceptedMimeTypes: IMAGE_MIME,
    onDrop: onBeforeUpload,
    onSuccess: onSourceFileUpload,
  });
  const isSourceFileUploading = activeSourceFileUploads.length > 0;
  const sourceFileUploadProgress = isSourceFileUploading
    ? activeSourceFileUploads[0].progress
    : undefined;

  const {
    input: attachmentInput,
    getDropzoneRootProps: getAttachmentRootProps,
    removeAttachment,
    selectAttachmentId,
    selectedAttachmentId,
    activeUploadsProgress,
    isUploading: isAttachmentsUploading,
    isDragging,
    isDragAccept,
    isDragReject,
    unlockVideoAttachmentsModal,
  } = useAttachmentManager({
    deviation,
    onSuccess: onAttachmentAdded,
  });

  const onRemoveAttachment = useCallback(
    (fileId: number) => {
      removeAttachment(fileId, () => {
        setDeviation({
          ...deviation,
          extended: {
            ...deviation?.extended,
            additionalMedia: deviation?.extended?.additionalMedia?.filter(
              media => media.fileId !== fileId
            ),
          },
        } as PapiDeviation);
      });
    },
    [removeAttachment, deviation]
  );

  const uploadProgress = sourceFileUploadProgress ?? activeUploadsProgress;
  const isUploading = isSourceFileUploading || isAttachmentsUploading;
  const showEmptyState = !deviation || deviation.isDefaultImage;
  const showPreview = !showEmptyState;

  return (
    <div className={cn(s['root'], isDragging && s['drag-active'], className)}>
      {unlockVideoAttachmentsModal}
      {sourceFileInput}
      {attachmentInput}

      {showEmptyState && (
        <EmptyState
          isOriginals={isOriginals}
          isUploading={isUploading}
          hasError={emptyStateHasError}
          uploadProgress={uploadProgress}
          getDropzoneRootProps={getSourceFileRootProps}
        />
      )}
      {showPreview && (
        <Preview
          deviation={deviation}
          getSourceFileRootProps={getSourceFileRootProps}
          getAttachmentRootProps={getAttachmentRootProps}
          uploadProgress={uploadProgress}
          isUploading={isUploading}
          isDragging={isDragging}
          isDragAccept={isDragAccept}
          isDragReject={isDragReject}
          selectedAttachmentId={selectedAttachmentId}
          onAttachmentAdded={onAttachmentAdded}
          onRemoveAttachment={onRemoveAttachment}
          onSelectAttachmentId={selectAttachmentId}
        />
      )}
    </div>
  );
};
MultiImageUploader.displayName = 'MultiImageUploader';

export default MultiImageUploader;
